import { LocationStore } from '../../location-store';
import React, { useEffect } from 'react';
import { useLocationList } from '../../hooks/use-location-list';
import { Flex, Box, Spinner, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { LocationListRow } from './location-list-row';
import { CommonSize, getOneSizeSmaller } from '../../../common/helpers/utils';

export interface LocationListProps {
    store: LocationStore;
    query: string;
    isLoadingResults: boolean;
    onClickRow?: (id: number) => void;
    moveFocusNext?: () => void;
    mode?: 'LIST' | 'MAP';
    size?: CommonSize;
    listBoxId?: string;
    optionIdPrefix: string;
}

export const LocationList = observer((props: LocationListProps) => {
    const { mode, isLoadingResults, query, store, onClickRow, moveFocusNext } = props;
    const locationList = useLocationList({ store, onClickRow, moveFocusNext });
    const {
        mark43Items,
        esriItems,
        esriCustomItems,
        latLngItems,
        forcedResults,
    } = locationList.data;
    const { onRowSelected } = locationList.operations;
    const smallerSize = getOneSizeSmaller(props.size);

    useEffect(() => {
        store.setSort(undefined);
    }, [store]);

    return (
        <Box
            h="100%"
            overflowY="auto"
            data-testid="LOCATION_LIST"
            borderRight="sm"
            borderColor="border.default"
            id={props.listBoxId}
            role="listbox"
        >
            {isLoadingResults ? (
                <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
                    <Spinner />
                </Flex>
            ) : (
                <Box size="md" width="100%">
                    {esriCustomItems.length > 0 ? (
                        <>
                            {esriCustomItems.map((row, index) => {
                                return (
                                    <LocationListRow
                                        onClick={() => onRowSelected(row)}
                                        key={row.id}
                                        row={row}
                                        query={query}
                                        store={store}
                                        index={index}
                                        mode={mode}
                                        size={smallerSize}
                                        optionIdPrefix={props.optionIdPrefix}
                                    />
                                );
                            })}
                        </>
                    ) : undefined}
                    {mark43Items.map((row, index) => {
                        return (
                            <LocationListRow
                                onClick={() => onRowSelected(row)}
                                key={row.id}
                                row={row}
                                query={query}
                                store={store}
                                index={index + esriCustomItems.length}
                                mode={mode}
                                size={smallerSize}
                                optionIdPrefix={props.optionIdPrefix}
                            />
                        );
                    })}
                    {esriItems.length > 0 ? (
                        <>
                            <Box pl={3} pt={3} color="brand.default" bg="surface.primary">
                                <Text fontSize={smallerSize}>Results from ESRI</Text>
                            </Box>
                            {esriItems.map((row, index) => {
                                return (
                                    <LocationListRow
                                        onClick={() => onRowSelected(row)}
                                        key={row.id}
                                        row={row}
                                        query={query}
                                        store={store}
                                        index={index + esriCustomItems.length + mark43Items.length}
                                        mode={mode}
                                        size={smallerSize}
                                        optionIdPrefix={props.optionIdPrefix}
                                    />
                                );
                            })}
                        </>
                    ) : undefined}
                    {latLngItems.length > 0 ? (
                        <>
                            {latLngItems.map((row, index) => {
                                return (
                                    <LocationListRow
                                        onClick={() => onRowSelected(row)}
                                        key={row.id}
                                        row={row}
                                        query={query}
                                        store={store}
                                        mode={mode}
                                        index={
                                            index +
                                            esriCustomItems.length +
                                            esriItems.length +
                                            mark43Items.length
                                        }
                                        size={smallerSize}
                                        optionIdPrefix={props.optionIdPrefix}
                                    />
                                );
                            })}
                        </>
                    ) : undefined}
                    {forcedResults.map((row, index) => {
                        return (
                            <LocationListRow
                                onClick={() => onRowSelected(row)}
                                key={row.id}
                                row={row}
                                query={query}
                                store={store}
                                index={
                                    index +
                                    esriCustomItems.length +
                                    mark43Items.length +
                                    esriItems.length +
                                    latLngItems.length
                                }
                                mode={mode}
                                size={smallerSize}
                                optionIdPrefix={props.optionIdPrefix}
                            />
                        );
                    })}
                </Box>
            )}
        </Box>
    );
});
